import { atom, useSetAtom, useStore } from "jotai";
import { useCallback, useEffect, useMemo } from "react";
import type { UID } from "#scripts/vmc/WorldState";

const isMac = typeof navigator !== "undefined" && navigator.platform.toUpperCase().indexOf("MAC") >= 0;

interface DevHoverOffset {
  x: number;
  y: number;
}

export interface DevHoverDetails {
  uid: UID;
  cmd: boolean;
  alt: boolean;
  isClick?: boolean;
  noPopup?: boolean;
}

interface DevHoverPosition {
  pageX: number;
  pageY: number;
}

const devHoverOffsetAtom = atom<DevHoverOffset>({ x: 10, y: 10 });
const devTargetPositionAtom = atom<DevHoverPosition>({ pageX: 0, pageY: 0 });
export const devHoverDetailsAtom = atom<DevHoverDetails | null>(null);
export const devHoverPositionAtom = atom<DevHoverPosition>((get) => ({
  pageX: get(devTargetPositionAtom).pageX + get(devHoverOffsetAtom).x,
  pageY: get(devTargetPositionAtom).pageY + get(devHoverOffsetAtom).y,
}));

export function useDevHoverReference(uid: UID) {
  const store = useStore();
  return useMemo(
    () => ({
      "data-uid": uid,
      onMouseEnter: (evt: React.MouseEvent<HTMLDivElement>) => {
        if (!evt.ctrlKey && !evt.metaKey && !evt.shiftKey) {
          store.set(devHoverDetailsAtom, {
            uid,
            alt: evt.altKey,
            cmd: false,
            noPopup: true,
          });
        }
      },
      onMouseLeave: () => {
        const current = store.get(devHoverDetailsAtom);
        if (current && current.uid === uid && current.noPopup) {
          store.set(devHoverDetailsAtom, null);
        }
      },
    }),
    [uid, store],
  );
}

export function useDevHover() {
  const setOffset = useSetAtom(devHoverOffsetAtom);
  const setHoverDetails = useSetAtom(devHoverDetailsAtom);
  const setTargetPosition = useSetAtom(devTargetPositionAtom);

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      const hoverMode = e.altKey || (isMac ? e.metaKey : e.ctrlKey);
      if (!hoverMode) return;
      e.preventDefault();
      setOffset((prev) => ({
        x: e.shiftKey ? prev.x + e.deltaX / 2 : prev.x,
        y: prev.y + e.deltaY,
      }));
    };

    window.addEventListener("wheel", handleWheel, {
      capture: true,
      passive: false,
    });
    return () => window.removeEventListener("wheel", handleWheel, { capture: true });
  }, [setOffset]);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const cmd = isMac ? e.metaKey : e.ctrlKey;
      const alt = e.altKey;

      const isHoverMode = (cmd && !alt) || (cmd && alt) || (!cmd && alt);

      if (!isHoverMode) {
        setHoverDetails((existing) => {
          if (existing?.noPopup) return existing;
          return null;
        });
        return;
      }

      const target = e.target as HTMLElement;

      if (cmd) {
        const uidElement = target.closest("[data-uid]");
        if (!uidElement) {
          setHoverDetails(null);
          return;
        }

        const uid = uidElement.getAttribute("data-uid") as UID;
        if (!uid) {
          setHoverDetails(null);
          return;
        }

        setTargetPosition({ pageX: e.pageX, pageY: e.pageY });
        setHoverDetails({
          uid,
          cmd,
          alt,
        });
        return;
      }

      setHoverDetails(null);
    },
    [setHoverDetails, setTargetPosition],
  );

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      const cmd = isMac ? e.metaKey : e.ctrlKey;
      const alt = e.altKey;

      if (!cmd) return;
      if (e.button === 1) return;

      const target = e.target as HTMLElement;
      const uidElement = target.closest("[data-uid]");
      if (!uidElement) return;

      const uid = uidElement.getAttribute("data-uid") as UID;
      if (!uid) return;

      setTargetPosition({ pageX: e.pageX, pageY: e.pageY });
      setHoverDetails({
        uid,
        cmd,
        alt,
        isClick: true,
      });

      e.preventDefault();
      e.stopPropagation();
    },
    [setHoverDetails, setTargetPosition],
  );

  return {
    handleMouseMove,
    handleClick,
    clearHover: () => {
      setHoverDetails(null);
      setTargetPosition({ pageX: 0, pageY: 0 });
    },
  };
}
